import type { InputSource } from './InputSystem'

/** "Close enough" threshold — once player is within this many px of the target, walk halts. */
const ARRIVAL_EPSILON = 4

/**
 * Tap-to-walk input source. On pointerdown, records the world-space tap target
 * (via the scene camera so zoom/pan are respected, not raw screen coords).
 * Each tick returns a unit vector from the player's current position toward the
 * target, or zero once the player is within ARRIVAL_EPSILON.
 *
 * A new tap replaces the previous target. `cancel()` clears the target — Plan 05
 * uses this when the player collides with something, to avoid the "pushing into
 * a wall" stuck state described in RESEARCH.md.
 */
export class TapToWalkSource implements InputSource {
  private target: { x: number; y: number } | null = null
  private readonly getPlayerPos: () => { x: number; y: number }

  constructor(scene: Phaser.Scene, getPlayerPos: () => { x: number; y: number }) {
    this.getPlayerPos = getPlayerPos
    scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      const wp = scene.cameras.main.getWorldPoint(p.x, p.y)
      this.target = { x: wp.x, y: wp.y }
    })
  }

  read(): { x: number; y: number } {
    if (!this.target) return { x: 0, y: 0 }
    const { x: px, y: py } = this.getPlayerPos()
    const dx = this.target.x - px
    const dy = this.target.y - py
    const dist = Math.hypot(dx, dy)
    if (dist < ARRIVAL_EPSILON) {
      this.target = null
      return { x: 0, y: 0 }
    }
    return { x: dx / dist, y: dy / dist }
  }

  /** Clears the active walk target. Called by Plan 05 on collision. */
  cancel(): void {
    this.target = null
  }
}
